// ============================================================================
// Create-coupon modal shared by the client-details Coupons section (single-use,
// one client) and Organisation → Coupons (general, any customer). The caller
// supplies the intro text, any extra fields and the API call; this owns the
// common fields, validation and backend error display.
//
// "Applies to" (scope_fields.tsx) sets the brand / category / product rules.
//
// "Copy settings from" picks any existing coupon as a template: it pre-fills the
// form (scope included), and the settings the form has no field for (area codes,
// T&C, max cart value, free delivery) are copied by the backend via
// `template_coupon_id`. Create always goes through a review step that shows
// every setting the new coupon will have.
//
// Pass `editing` (the coupon's detail) to edit instead: the form opens with its
// current values, there is no template picker, and the settings the form has no
// field for are shown as unchanged.
// ============================================================================
import {
  Button,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Select,
  Spin,
  Switch,
  Tag,
} from "antd";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CouponTemplateDetail,
  CouponTemplateOption,
  fetchCouponTemplate,
  searchCouponTemplates,
} from "../../../apis/client.api";
import {
  AnyRuleNote,
  restrictedRuleCount,
  SCOPE_DEFAULTS,
  SCOPE_KEYS,
  ScopeBody,
  ScopeFields,
  scopeFromTemplate,
  scopeRows,
  ScopeSummaryData,
  scopeToBody,
} from "./scope_fields";

export const inr = new Intl.NumberFormat("en-IN", {
  style: "currency", currency: "INR", maximumFractionDigits: 0,
});

export const discountLabel = (c: { type: string; coupon_value: number; upto_value: number }) =>
  c.type === "percentage"
    ? `${c.coupon_value}%${c.upto_value > 0 ? ` up to ${inr.format(c.upto_value)}` : ""}`
    : `${inr.format(c.coupon_value)} off`;

const CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no 0/O/1/I — read out over the phone
const generateCode = () =>
  "MD" +
  Array.from({ length: 6 }, () => CODE_CHARS[Math.floor(Math.random() * CODE_CHARS.length)]).join("");

// DRF serializer errors arrive as { errors: { field: [msg] } } or { errors: { non_field_errors: [msg] } }.
const backendError = (err: any): string => {
  const body = err?.response?.data;
  const errors = body?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat()[0];
    if (first) return String(first);
  }
  return body?.message || "Could not create coupon. Please try again.";
};

// Common create body, already normalised for the backend.
export interface CouponFormBody extends ScopeBody {
  coupon_code: string;
  type: "percentage" | "value";
  coupon_value: number;
  upto_value: number;
  min_cart_value: number;
  cumulative_sum_above: number;
  valid_till: string; // YYYY-MM-DD
  description: string;
  is_free_delivery: boolean;
  template_coupon_id?: number;
}

const STATUS_COLOR: Record<string, string> = { active: "green", expired: "default", inactive: "default" };

// The settings copied from the template that the form doesn't show.
const CopiedSettings: React.FC<{ t: CouponTemplateDetail }> = ({ t }) => (
  <Descriptions size="small" column={1} bordered>
    <Descriptions.Item label="Max order value">
      {t.max_cart_value > 0 ? inr.format(t.max_cart_value) : "No limit"}
    </Descriptions.Item>
    <Descriptions.Item label="Area codes">
      {t.area_codes.length ? t.area_codes.join(", ") : "All areas"}
    </Descriptions.Item>
    <Descriptions.Item label="Terms & conditions">
      <span style={{ whiteSpace: "pre-wrap" }}>{t.terms_and_conditions || "—"}</span>
    </Descriptions.Item>
  </Descriptions>
);

interface Props {
  open: boolean;
  title: string;
  intro: React.ReactNode;
  descriptionPlaceholder?: string;
  // Extra Form.Items (e.g. a "hidden" switch); their values arrive in `extra`.
  extraFields?: React.ReactNode;
  // Review-step rows for the extra fields.
  describeExtra?: (extra: Record<string, any>) => { label: string; value: React.ReactNode }[];
  editing?: CouponTemplateDetail | null;
  onClose: () => void;
  onSubmit: (body: CouponFormBody, extra: Record<string, any>) => Promise<unknown>;
  onCreated: () => void;
}

const CouponFormModal: React.FC<Props> = ({
  open,
  title,
  intro,
  descriptionPlaceholder,
  extraFields,
  describeExtra,
  editing,
  onClose,
  onSubmit,
  onCreated,
}) => {
  const [form] = Form.useForm();
  const type = Form.useWatch("type", form);
  const uptoValue = Form.useWatch("upto_value", form);
  // Pricing only honours cumulative_sum_above on uncapped percentage coupons
  // (order/helpers.py get_total_discount_in_order), so only offer it there.
  const allowsCumulative = type === "percentage" && !(uptoValue > 0);
  const [saving, setSaving] = useState(false);
  const [review, setReview] = useState<{
    body: CouponFormBody;
    extra: Record<string, any>;
    scope: ScopeSummaryData;
  } | null>(null);
  const brandMode = Form.useWatch("brand_mode", form);
  const categoryMode = Form.useWatch("category_mode", form);
  const variantMode = Form.useWatch("variant_mode", form);
  const liveRestricted = [brandMode, categoryMode, variantMode].filter((m) => m && m !== "all").length;

  // --- template ("copy settings from") ---------------------------------------
  const [template, setTemplate] = useState<CouponTemplateDetail | null>(null);
  const [options, setOptions] = useState<CouponTemplateOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const searchSeq = useRef(0);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  const runSearch = (q: string) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      const seq = ++searchSeq.current;
      setSearching(true);
      const rows = await searchCouponTemplates(q);
      if (seq === searchSeq.current) {
        setOptions(rows);
        setSearching(false);
      }
    }, 300);
  };

  useEffect(() => {
    setTemplate(null);
    setReview(null);
    if (open && !editing) runSearch("");
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyTemplate = async (couponId?: number) => {
    if (!couponId) {
      setTemplate(null);
      return;
    }
    setLoadingTemplate(true);
    const t = await fetchCouponTemplate(couponId);
    setLoadingTemplate(false);
    if (!t) return;
    setTemplate(t);
    // Code and validity stay fresh; everything else the form edits comes from the template.
    form.setFieldsValue({
      type: t.type,
      coupon_value: t.coupon_value,
      upto_value: t.upto_value || undefined,
      min_cart_value: t.min_cart_value || undefined,
      cumulative_sum_above: t.cumulative_sum_above || undefined,
      description: t.description || undefined,
      is_hidden: t.is_hidden,
      is_free_delivery: t.is_free_delivery,
      ...scopeFromTemplate(t),
    });
  };

  // Fresh defaults each time the modal opens; destroyOnClose remounts the form with them.
  const initialValues = useMemo(() => {
    if (!open) return {};
    if (editing) {
      return {
        coupon_code: editing.coupon_code,
        type: editing.type,
        coupon_value: editing.coupon_value,
        upto_value: editing.upto_value || undefined,
        min_cart_value: editing.min_cart_value || undefined,
        cumulative_sum_above: editing.cumulative_sum_above || undefined,
        valid_till: dayjs(editing.valid_till),
        description: editing.description || undefined,
        is_hidden: editing.is_hidden,
        is_free_delivery: editing.is_free_delivery,
        ...scopeFromTemplate(editing),
      };
    }
    return {
      coupon_code: generateCode(),
      type: "percentage",
      valid_till: dayjs().add(30, "day"),
      is_free_delivery: false,
      ...SCOPE_DEFAULTS,
    };
  }, [open, editing]);
  // Settings the form can't change: copied from the template on create, kept on edit.
  const fixedSettings = editing ?? template;

  // Step 1: validate and show the review. Step 2 (confirm) creates.
  const handleReview = async () => {
    const values = await form.validateFields();
    const {
      coupon_code, type, coupon_value, upto_value, min_cart_value, cumulative_sum_above,
      valid_till, description, is_free_delivery, ...rest
    } = values;
    const extra = Object.fromEntries(Object.entries(rest).filter(([k]) => !SCOPE_KEYS.includes(k)));
    const scope = scopeToBody(values);
    const capped = type === "percentage" && upto_value > 0;
    setReview({
      scope: scope.summary,
      body: {
        ...scope.body,
        coupon_code: coupon_code.trim().toUpperCase(),
        type,
        coupon_value,
        upto_value: type === "percentage" ? upto_value ?? 0 : 0,
        min_cart_value: min_cart_value ?? 0,
        cumulative_sum_above: type === "percentage" && !capped ? cumulative_sum_above ?? 0 : 0,
        valid_till: valid_till.format("YYYY-MM-DD"),
        description: description?.trim() || "",
        is_free_delivery: !!is_free_delivery,
        ...(template && !editing ? { template_coupon_id: template.coupon_id } : {}),
      },
      extra,
    });
  };

  const handleCreate = async () => {
    if (!review) return;
    setSaving(true);
    try {
      await onSubmit(review.body, review.extra);
      message.success(editing ? "Coupon updated" : "Coupon created");
      onClose();
      onCreated();
    } catch (err) {
      message.error(backendError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={review ? "Review coupon" : title}
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={680}
      footer={
        review
          ? [
              <Button key="back" onClick={() => setReview(null)} disabled={saving}>
                Back
              </Button>,
              <Button key="create" type="primary" loading={saving} onClick={handleCreate}>
                {editing ? "Confirm & save" : "Confirm & create"}
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose}>
                Cancel
              </Button>,
              <Button key="review" type="primary" onClick={handleReview} disabled={loadingTemplate}>
                Review
              </Button>,
            ]
      }
    >
      {review && (
        <>
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Code">
              <b style={{ fontFamily: "monospace" }}>{review.body.coupon_code}</b>
            </Descriptions.Item>
            <Descriptions.Item label="Discount">{discountLabel(review.body)}</Descriptions.Item>
            <Descriptions.Item label="Min order value">
              {review.body.min_cart_value > 0 ? inr.format(review.body.min_cart_value) : "None"}
            </Descriptions.Item>
            <Descriptions.Item label="Cumulative purchase above">
              {review.body.cumulative_sum_above > 0
                ? `${inr.format(review.body.cumulative_sum_above)} (delivered, last 3 months)`
                : "None"}
            </Descriptions.Item>
            <Descriptions.Item label="Valid till">
              {dayjs(review.body.valid_till).format("DD MMM YYYY")}
            </Descriptions.Item>
            <Descriptions.Item label="Free delivery">
              {review.body.is_free_delivery ? "Yes — delivery charges set to ₹0" : "No"}
            </Descriptions.Item>
            <Descriptions.Item label="Note">{review.body.description || "—"}</Descriptions.Item>
            {(describeExtra?.(review.extra) ?? []).map((row) => (
              <Descriptions.Item key={row.label} label={row.label}>
                {row.value}
              </Descriptions.Item>
            ))}
          </Descriptions>
          <div className="th-font-weight-600" style={{ marginBottom: 8 }}>
            Applies to
          </div>
          {restrictedRuleCount(review.scope) > 1 && <AnyRuleNote />}
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
            {scopeRows(review.scope).map((row) => (
              <Descriptions.Item key={row.label} label={row.label}>
                {row.value}
              </Descriptions.Item>
            ))}
          </Descriptions>
          {fixedSettings ? (
            <>
              <div className="th-font-weight-600" style={{ marginBottom: 8 }}>
                {editing ? "Not changed by this edit" : `Copied from ${fixedSettings.coupon_code}`}
              </div>
              <CopiedSettings t={fixedSettings} />
            </>
          ) : (
            <p className="text-muted" style={{ margin: 0 }}>
              All areas. No max order value, no T&amp;C.
            </p>
          )}
        </>
      )}
      <div style={{ display: review ? "none" : undefined }}>
        <p className="text-muted" style={{ marginBottom: 16 }}>
          {intro}
        </p>
        <div style={{ marginBottom: 16, display: editing ? "none" : undefined }}>
          <div style={{ marginBottom: 8 }}>Copy settings from (optional)</div>
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Search an existing coupon by code or note"
            filterOption={false}
            onSearch={runSearch}
            onChange={(v) => applyTemplate(v as number | undefined)}
            loading={searching || loadingTemplate}
            notFoundContent={searching ? <Spin size="small" /> : "No coupons found"}
            options={options.map((o) => ({
              value: o.coupon_id,
              label: (
                <span>
                  <b style={{ fontFamily: "monospace" }}>{o.coupon_code}</b> · {discountLabel(o)}{" "}
                  <Tag color={STATUS_COLOR[o.status]}>{o.status}</Tag>
                  {o.is_user_restricted && <Tag>client</Tag>}
                  {o.brand_count + o.variant_count + o.category_count > 0 && (
                    <span className="text-muted">
                      {o.brand_count} brands · {o.variant_count} products · {o.category_count} categories
                    </span>
                  )}
                </span>
              ),
            }))}
          />
          {template && (
            <div style={{ marginTop: 12 }}>
              <div className="text-muted" style={{ marginBottom: 8 }}>
                The fields below, including "Applies to", are filled from {template.coupon_code};
                edit them as needed. These settings are copied as-is:
              </div>
              <CopiedSettings t={template} />
            </div>
          )}
        </div>
        <Form form={form} layout="vertical" requiredMark={false} initialValues={initialValues} preserve={false}>
          <Form.Item
            label="Coupon code"
            name="coupon_code"
            normalize={(v: string) => v?.toUpperCase().replace(/\s/g, "")}
            rules={[
              { required: true, message: "Enter a code" },
              { pattern: /^[A-Z0-9_-]{4,30}$/, message: "4–30 letters, numbers, - or _" },
            ]}
          >
            <Input
              maxLength={30}
              style={{ fontFamily: "monospace" }}
              addonAfter={
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: "auto" }}
                  onClick={() => form.setFieldValue("coupon_code", generateCode())}
                >
                  Regenerate
                </Button>
              }
            />
          </Form.Item>
          <Form.Item label="Discount type" name="type">
            <Radio.Group
              optionType="button"
              options={[
                { value: "percentage", label: "Percentage (%)" },
                { value: "value", label: "Flat amount (₹)" },
              ]}
              onChange={() => form.validateFields(["coupon_value"]).catch(() => undefined)}
            />
          </Form.Item>
          <Form.Item
            label={type === "percentage" ? "Discount %" : "Discount amount (₹)"}
            name="coupon_value"
            rules={[
              { required: true, message: "Enter the discount" },
              {
                validator: (_, v) =>
                  v == null || (v > 0 && (type !== "percentage" || v <= 100))
                    ? Promise.resolve()
                    : Promise.reject(
                        type === "percentage" ? "Between 1 and 100" : "Must be more than 0"
                      ),
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              precision={0}
              min={1}
              max={type === "percentage" ? 100 : undefined}
              addonAfter={type === "percentage" ? "%" : "₹"}
            />
          </Form.Item>
          {type === "percentage" && (
            <Form.Item
              label="Max discount (₹, optional)"
              name="upto_value"
              extra="Caps the discount, e.g. 10% up to ₹5,000. Leave blank for no cap."
            >
              <InputNumber style={{ width: "100%" }} precision={0} min={0} addonAfter="₹" />
            </Form.Item>
          )}
          <Form.Item label="Minimum order value (₹, optional)" name="min_cart_value">
            <InputNumber style={{ width: "100%" }} precision={0} min={0} addonAfter="₹" />
          </Form.Item>
          {allowsCumulative && (
            <Form.Item
              label="Cumulative purchase above (₹, optional)"
              name="cumulative_sum_above"
              extra="The discount applies only when the customer's delivered orders in the last 3 months plus this order exceed this amount. Not available with a max discount cap."
            >
              <InputNumber style={{ width: "100%" }} precision={0} min={0} addonAfter="₹" />
            </Form.Item>
          )}
          <Form.Item
            label="Free delivery"
            name="is_free_delivery"
            valuePropName="checked"
            extra="Sets standard delivery charges to ₹0 when the coupon applies (order meets the minimum and has at least one item the coupon covers). Express delivery charges on PIs are not waived."
          >
            <Switch />
          </Form.Item>
          <ScopeFields />
          {liveRestricted > 1 && <AnyRuleNote />}
          <Form.Item
            label="Valid till"
            name="valid_till"
            rules={[
              { required: true, message: "Pick an expiry date" },
              {
                // An edited coupon may already be expired; the backend needs a date from today on.
                validator: (_, d) =>
                  !d || !d.isBefore(dayjs(), "day")
                    ? Promise.resolve()
                    : Promise.reject("Pick today or a later date"),
              },
            ]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD MMM YYYY"
              disabledDate={(d) => d.isBefore(dayjs(), "day")}
            />
          </Form.Item>
          <Form.Item label="Note (optional)" name="description">
            <Input.TextArea rows={2} maxLength={500} placeholder={descriptionPlaceholder} />
          </Form.Item>
          {extraFields}
        </Form>
      </div>
    </Modal>
  );
};

export default CouponFormModal;
